import { statDueRange } from "../../works/board/worksBoard.utils";

export type HomeView = "mine" | "firm";

const DAY = 24 * 60 * 60 * 1000;

export const startOfDay = (date: Date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

export const endOfDay = (date: Date) => {
  const day = new Date(date);
  day.setHours(23, 59, 59, 999);
  return day;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

/** Whole calendar days from a to b (b later = positive). */
export const daysBetween = (a: Date, b: Date) =>
  Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY);

export const greeting = (now: Date = new Date()) => {
  const hour = now.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

/**
 * Due-date ranges behind the cards and lists. "This week" ends on Sunday,
 * like the Work page; "last" is the same range one week earlier.
 */
export const homeRanges = (today: Date = new Date()) => {
  const weekAgo = addDays(today, -7);
  const dueWeek = statDueRange("dueWeek", today)!;
  const dueWeekLast = statDueRange("dueWeek", weekAgo)!;
  // Monday of this week (completed work is counted from Monday on).
  const monday = startOfDay(addDays(today, -((today.getDay() + 6) % 7)));
  return {
    today,
    weekAgo,
    overdue: statDueRange("overdue", today)!,
    dueToday: { fromDate: startOfDay(today), toDate: endOfDay(today) },
    dueTodayLast: { fromDate: startOfDay(weekAgo), toDate: endOfDay(weekAgo) },
    dueWeek,
    dueWeekLast,
    upcoming: {
      fromDate: startOfDay(addDays(today, 1)),
      toDate: endOfDay(addDays(today, 7)),
    },
    completedWeek: { from: monday, to: today },
    completedLastWeek: { from: addDays(monday, -7), to: weekAgo },
    // Completed work is looked up by due date around now (see the hook).
    completedLookup: {
      fromDate: startOfDay(addDays(today, -90)),
      toDate: endOfDay(addDays(today, 365)),
    },
  };
};

/** "16d late" or "Due today" (Needs attention today). */
export const attentionLabel = (
  due: Date | string,
  today: Date = new Date(),
) => {
  const late = daysBetween(new Date(due), today);
  return late > 0 ? `${late}d late` : "Due today";
};

/** "in 4 days" / "Tomorrow" (Upcoming deadlines). */
export const inDaysLabel = (due: Date | string, today: Date = new Date()) => {
  const days = daysBetween(today, new Date(due));
  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `in ${days} days`;
};

export type Delta = { text: string; tone: "good" | "bad" | "flat" };

/**
 * "↑ +3 vs last" compared with the same period last week. For overdue and
 * due-today more is worse; for completed more is better.
 */
export const compareWithLast = (
  current: number | undefined,
  last: number | undefined,
  { moreIsBetter = false, percent = false } = {},
): Delta | null => {
  if (current === undefined || last === undefined) return null;
  const diff = current - last;
  if (diff === 0)
    return current ? { text: "same as last week", tone: "flat" } : null;
  const amount =
    percent && last > 0
      ? `${Math.round((Math.abs(diff) / last) * 100)}%`
      : `${Math.abs(diff)}`;
  const up = diff > 0;
  return {
    text: `${up ? "↑ +" : "↓ −"}${amount} vs last`,
    tone: up === moreIsBetter ? "good" : "bad",
  };
};

/** Work whose status became Completed between two moments. */
export const countCompletedBetween = (
  works: { statusUpdatedAt: string | null }[] | undefined,
  from: Date,
  to: Date,
) =>
  works === undefined
    ? undefined
    : works.filter((work) => {
        if (!work.statusUpdatedAt) return false;
        const at = new Date(work.statusUpdatedAt).getTime();
        return at >= from.getTime() && at <= to.getTime();
      }).length;

/** Fill of a pipeline bar: share of the biggest stage. */
export const barShare = (
  count: number | undefined,
  counts: (number | undefined)[],
) => {
  const max = Math.max(0, ...counts.map((value) => value ?? 0));
  return max ? Math.round(((count ?? 0) / max) * 100) : 0;
};

/** Inbox time: "9:42 AM" today, "Yesterday", else "Oct 17". */
export const inboxTimeLabel = (unixSeconds: number, now: Date = new Date()) => {
  const at = new Date(unixSeconds * 1000);
  const days = daysBetween(at, now);
  if (days <= 0)
    return at.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  if (days === 1) return "Yesterday";
  return at.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const DEADLINE_COLORS = ["#C2185B", "#7C66F0", "#D35400", "#0E7490", "#2E7D32"];

/** A steady color per work type (Upcoming deadlines). */
export const workTypeColor = (workType: string | null | undefined) => {
  const name = workType || "";
  let hash = 0;
  for (let i = 0; i < name.length; i += 1)
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return DEADLINE_COLORS[hash % DEADLINE_COLORS.length];
};

export type PipelineStage = {
  key: string;
  label: string;
  /** Main status group (Ready, In Progress, Waiting…); null for "Other". */
  group: string | null;
  color: string;
  count: number;
};

const OTHER_COLOR = "#B6BCC8";

/**
 * The most used statuses among active work: a sub-status ("Final Review")
 * or, without one, its main group ("Ready"), colored like the group. The
 * rest is added up under "Other".
 */
export const topStatuses = (
  works: { jobStatus: string; jobSubStatus: string | null }[],
  groupColors: Record<string, string>,
  limit = 5,
): PipelineStage[] => {
  const sorted = rankStatuses(works, groupColors);
  // With one stage more than the limit, show it instead of "Other (1 status)".
  if (sorted.length <= limit + 1) return sorted;
  const rest = sorted.slice(limit).reduce((sum, stage) => sum + stage.count, 0);
  return [
    ...sorted.slice(0, limit),
    {
      key: "other",
      label: "Other",
      group: null,
      color: OTHER_COLOR,
      count: rest,
    },
  ];
};

/**
 * Every status that has active work, most used first (a sub-status, or the
 * main group when there is none). Statuses without work never appear.
 */
export const rankStatuses = (
  works: { jobStatus: string; jobSubStatus: string | null }[],
  groupColors: Record<string, string>,
): PipelineStage[] => {
  const stages = new Map<string, PipelineStage>();
  works.forEach((work) => {
    const label = work.jobSubStatus?.trim() || work.jobStatus;
    const key = `${work.jobStatus}|${label}`;
    const stage = stages.get(key) ?? {
      key,
      label,
      group: work.jobStatus,
      color: groupColors[work.jobStatus.toLowerCase()] || OTHER_COLOR,
      count: 0,
    };
    stage.count += 1;
    stages.set(key, stage);
  });
  return [...stages.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
};

/** Home pipeline: 6 statuses at a time, ‹ › move between the groups. */
export const PIPELINE_PAGE_SIZE = 6;

export const pipelinePage = <T>(
  stages: T[],
  page: number,
  size = PIPELINE_PAGE_SIZE,
) => {
  const pages = Math.max(1, Math.ceil(stages.length / size));
  const current = Math.min(Math.max(0, page), pages - 1);
  return {
    stages: stages.slice(current * size, current * size + size),
    page: current,
    pages,
    hasPrevious: current > 0,
    hasNext: current < pages - 1,
  };
};

/**
 * Needs attention today: overdue work, then work due today, each item once.
 * The server's day boundaries can differ from the browser's time zone, so
 * the split is redone here by the work's own due date.
 */
export const mergeAttention = <T extends { workId: string; dueDate: string }>(
  overdue: T[],
  dueToday: T[],
  today: Date = new Date(),
) => {
  const seen = new Set<string>();
  const all = [...overdue, ...dueToday].filter((work) => {
    if (seen.has(work.workId)) return false;
    seen.add(work.workId);
    return true;
  });
  const late = (work: T) => daysBetween(new Date(work.dueDate), today) > 0;
  return [...all.filter(late), ...all.filter((work) => !late(work))];
};

/**
 * "Due today" = due on that calendar day in the browser's time zone and not Completed, the
 * same rule as the Deadlines report ("Today") (QA DATA-008: the card trusted the server's
 * date range and showed 3 where the report showed 2).
 */
export const onlyDueOn = <
  T extends { dueDate: string; jobStatus?: string | null },
>(
  works: T[],
  day: Date,
): T[] =>
  works.filter((work) => {
    const due = new Date(work.dueDate);
    if (Number.isNaN(due.getTime())) return false;
    if ((work.jobStatus || "").trim().toLowerCase().startsWith("completed"))
      return false;
    return daysBetween(day, due) === 0;
  });

/**
 * Count for a "due on a day" card from a loaded page: exact when the page holds every match
 * (server total <= rows loaded), else the server's total (more than a page can't be checked).
 */
export const dueOnCount = <
  T extends {
    dueDate: string;
    jobStatus?: string | null;
    totalRecords?: number;
  },
>(
  works: T[] | undefined,
  day: Date,
): number | undefined => {
  if (!works) return undefined;
  const total = works[0]?.totalRecords ?? works.length;
  return total > works.length ? total : onlyDueOn(works, day).length;
};
